import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "../../shared/stores/RootStore";
import { CampaignApi } from "../../project/services/CampaignApi";
import {
  buildEmptyUpdateHlaWay,
  CreateHlaWay,
  HlaWay,
  hlaWayToUpdate,
  UpdateHlaWay,
} from "../models/HlaWay";
import { HlaWayApi } from "../services/HlaWayApi";
import { MergeHlaWayInto } from "../models/MergeHlaWayInto";
import { HlaLane } from "../models/HlaLane";
import { LngLat } from "mapbox-gl";

interface AdjacentToSelected {
  previous: HlaWay[];
  next: HlaWay[];
}

export class HlaStore {
  rootStore;

  // data and selection
  ways: HlaWay[] = [];
  selectedId?: string;
  multiSelectedIds = new Set<string>();

  // form
  form: UpdateHlaWay = buildEmptyUpdateHlaWay();
  defaultToFrontFacing = true;
  adjacentToSelected = defaultAdjacentToSelected();
  dirtyIds = new Set<string>();
  savingChanges = false;

  // pagination
  pageSize = 50;
  nextPage: number | null = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.ways = [];
      this.savingChanges = false;
      this.dirtyIds = new Set();
      this.selectedId = undefined;
      this.nextPage = 0;
      this.adjacentToSelected = defaultAdjacentToSelected();
    });
  }

  get(hlaWayId: string): HlaWay | undefined {
    const ways = this.getWays();
    // TODO can be optimized by using indexing
    return ways.find((way) => way.id === hlaWayId);
  }

  getWays(): HlaWay[] {
    const { hlaForecastStore } = this.rootStore;
    return hlaForecastStore.computedValues?.hlaWays ?? this.ways;
  }

  getSelected(): HlaWay | undefined {
    if (!this.selectedId) return;
    return this.get(this.selectedId);
  }

  getSelectedLane(): HlaLane | undefined {
    const selectedWay = this.getSelected();
    if (!selectedWay) return;
    if (!this.defaultToFrontFacing && selectedWay.backFacingLane)
      return selectedWay.backFacingLane;
    else if (selectedWay.frontFacingLane) return selectedWay.frontFacingLane;
    return undefined;
  }

  setDefaultToFrontFacing(value: boolean) {
    runInAction(() => {
      this.defaultToFrontFacing = value;
    });
  }

  get changesSaved() {
    return this.dirtyIds.size === 0;
  }

  select(hlaWayId: string) {
    const { osaWayStore } = this.rootStore;
    osaWayStore.clearSelected();

    runInAction(() => (this.selectedId = hlaWayId));
    this.updateMapSource();
    this.updateAdjacentToSelected();
  }

  clearSelected() {
    runInAction(() => (this.selectedId = undefined));
    this.updateMapSource();
  }

  setMultiSelectedIds(value: Set<string>) {
    runInAction(() => (this.multiSelectedIds = value));
    this.updateMapSource();
  }

  toggleMultiSelectedId(nodeId: string) {
    const newSet = new Set(this.multiSelectedIds);
    if (newSet.has(nodeId)) {
      newSet.delete(nodeId);
    } else {
      newSet.add(nodeId);
    }
    this.setMultiSelectedIds(newSet);
  }

  updateAdjacentToSelected() {
    const way = this.getSelected();
    const previousWays: HlaWay[] = [];
    const nextWays: HlaWay[] = [];

    if (way) {
      const coordsMatch = (a: number[], b: number[]) =>
        a[0] === b[0] && a[1] === b[1];

      const coords = way.coordinates;

      this.ways.forEach((otherWay) => {
        if (way.id === otherWay.id) return;
        const otherCoords = otherWay.coordinates;

        if (coordsMatch(coords[coords.length - 1], otherCoords[0])) {
          nextWays.push(otherWay);
        } else if (
          coordsMatch(coords[0], otherCoords[otherCoords.length - 1])
        ) {
          previousWays.push(otherWay);
        }
      });
    }

    runInAction(() => {
      this.adjacentToSelected = {
        previous: previousWays,
        next: nextWays,
      };
    });
  }

  verify() {
    const selectedWay = this.getSelected();
    if (!selectedWay) return;

    runInAction(() => {
      this.form = { ...this.form, verified: true };
      selectedWay.verified = true;
    });
  }

  code(callback: (prev: UpdateHlaWay) => UpdateHlaWay) {
    var selectedIds = this.selectedId ? [this.selectedId] : [];
    if (this.multiSelectedIds.size) {
      selectedIds = [...this.multiSelectedIds];
    }

    runInAction(() => {
      this.form = callback(this.form);

      this.ways = this.ways.map((way) => {
        if (selectedIds.includes(way.id)) {
          this.markAsDirty(way.id);
          return callback(way) as HlaWay;
        }
        return way;
      });
    });
  }

  setForm(value: UpdateHlaWay) {
    runInAction(() => {
      this.form = value;
    });
  }

  async create(body: CreateHlaWay) {
    const { data: way } = await HlaWayApi.create(body);
    runInAction(() => {
      this.setWays([...this.ways, way]);
    });
    return way;
  }

  async refreshWay(hlaWayId: string) {
    const { data: way } = await HlaWayApi.get(hlaWayId);
    runInAction(() => {
      this.ways = this.ways.map((r) => (r.id === hlaWayId ? way : r));
    });
  }

  setWays(list: HlaWay[]) {
    const { filterStore } = this.rootStore;
    runInAction(() => {
      this.ways = list;
    });
    filterStore.filterHlaWays();
  }

  updateMapSource() {
    const { mapStore } = this.rootStore;
    mapStore.hlaMapSource?.onStoreUpdate();
  }

  async fetchAll(campaignId: string, cancelIf?: () => boolean) {
    this.setWays([]);
    runInAction(() => {
      this.nextPage = 0;
    });
    while (this.nextPage !== null) {
      const response = await CampaignApi.pageHlaWays(
        campaignId,
        this.pageSize,
        this.nextPage,
      );
      if (cancelIf?.()) return;
      this.setWays(this.ways.concat(response.data.data));
      runInAction(() => {
        this.nextPage = response.data.meta.nextPage;
      });
    }
  }

  async delete(id: string) {
    await HlaWayApi.delete(id);
    this.setWays(this.ways.filter((way) => way.id !== id));
  }

  async saveChanges() {
    runInAction(() => {
      this.savingChanges = true;
    });

    try {
      for (const wayId of this.dirtyIds) {
        const hlaWay = this.get(wayId);

        if (!hlaWay) continue;

        await this.update(hlaWay.id, hlaWayToUpdate(hlaWay));
      }

      runInAction(() => {
        this.dirtyIds = new Set();
      });
    } finally {
      runInAction(() => {
        this.savingChanges = false;
      });
    }
  }

  async update(id: string, body: UpdateHlaWay) {
    const { data: updatedWay } = await HlaWayApi.update(id, body);
    this.setWays(this.ways.map((way) => (way.id === id ? updatedWay : way)));
  }

  async mergeInto(id: string, body: MergeHlaWayInto) {
    await HlaWayApi.mergeInto(id, body).then(({ data: targetHlaWay }) => {
      this.setWays(
        this.ways
          .filter((way) => way.id !== id)
          .map((way) => (way.id === targetHlaWay.id ? targetHlaWay : way)),
      );
      this.select(targetHlaWay.id);
    });
  }

  private markAsDirty = (wayId: string) => {
    runInAction(() => {
      this.dirtyIds.add(wayId);
    });
  };

  async cutSelectedWay(newLines: LngLat[][]) {
    const wayId = this.selectedId;
    if (!wayId || newLines.length < 2) return;

    return await HlaWayApi.cut(wayId, newLines).then(({ data: ways }) => {
      ways.forEach((way) => {
        if (way.id === wayId) {
          this.setWays(this.ways.map((w) => (w.id === way.id ? way : w)));
        } else {
          this.setWays([...this.ways, way]);
        }
      });

      return ways;
    });
  }
}

const defaultAdjacentToSelected = (): AdjacentToSelected => ({
  previous: [],
  next: [],
});
